import { useState } from "react";
import { useAuth } from "context/AuthContext";
import useAdminMutation from "layouts/wallet/useAdminMutation";
import { toast } from "react-toastify";
import {
  Badge,
  DataTable,
  Field,
  LoadState,
  Modal,
  Page,
  Panel,
  Stats,
  dateTime,
  money,
  useAdminData,
} from "layouts/finance/shared";
import {
  downloadReferralsCsv,
  referralApi,
} from "services/ReferralService";

const REFERRAL_STATUSES = ["", "SIGNED_UP", "ONBOARDED", "TRANSACTING"];
const REFERRAL_CODE_PATTERN = /^[A-Z0-9_-]{3,20}$/;

const pageRows = (data) => (Array.isArray(data?.content) ? data.content : []);
const leaderboardRows = (data) => (Array.isArray(data) ? data : []);
const estimatedPayout = (row) =>
  ((row.totalReferred || 0) + (row.totalTransacting || 0)) * 15000;
const referrerCode = (referrers, merchantId) =>
  referrers.find((referrer) => referrer.referrerMerchantId === merchantId)
    ?.referralCode || "";

function ReferralStatusBadge({ value }) {
  const tone = {
    SIGNED_UP: "amber",
    ONBOARDED: "blue",
    TRANSACTING: "green",
  }[value] || "gray";
  return (
    <span className={`status-badge ${tone}`}>
      {String(value || "Unavailable").replace(/_/g, " ")}
    </span>
  );
}

function ReferralCodeDialog({ referrer, onClose, onSave, busy, error }) {
  const [code, setCode] = useState(referrer.referralCode || "");
  const normalizedCode = code.trim().toUpperCase();
  const isValid = REFERRAL_CODE_PATTERN.test(normalizedCode);

  return (
    <Modal
      title={`Assign referral code to ${referrer.referrerName}`}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        onSave(normalizedCode);
      }}
      busy={busy}
      disabled={!isValid}
      action="Save referral code"
    >
      <p>
        Codes must be 3–20 characters and use uppercase letters, numbers,
        underscores, or dashes. The code must also be globally unique.
      </p>
      <Field
        label="Referral code"
        name="referralCode"
        value={code}
        onChange={(event) => setCode(event.target.value.toUpperCase())}
        placeholder="e.g. BD-TUNDE"
        maxLength={20}
        autoFocus
        aria-invalid={Boolean(code) && !isValid}
      />
      {code && !isValid && (
        <p className="notice error" role="alert">
          Enter 3–20 uppercase letters, numbers, underscores, or dashes.
        </p>
      )}
      {error && (
        <div className="notice error" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}

export default function Referrals() {
  const { token, logout } = useAuth();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [status, setStatus] = useState("");
  const [referrerIdInput, setReferrerIdInput] = useState("");
  const [referrerId, setReferrerId] = useState("");
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [exporting, setExporting] = useState(false);

  const list = useAdminData(
    referralApi,
    "",
    {
      page,
      size,
      ...(status ? { status } : {}),
      ...(referrerId ? { referrerId: Number(referrerId) } : {}),
    }
  );
  const leaderboard = useAdminData(referralApi, "/summary");
  const refresh = () => {
    list.refresh();
    leaderboard.refresh();
  };
  const mutation = useAdminMutation(refresh);
  const referrals = pageRows(list.data);
  const leaderboardData = leaderboardRows(leaderboard.data);
  const totalPages = list.data?.totalPages;

  const assignCode = (referralCode) => {
    mutation.submit(
      referralApi,
      `/${encodeURIComponent(selectedReferrer.referrerMerchantId)}/code`,
      { method: "PATCH", data: { referralCode } },
      () => {
        setSelectedReferrer(null);
        mutation.reset();
      },
      "Referral code updated successfully."
    );
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadReferralsCsv(token);
    } catch (error) {
      if (error.status === 401) logout();
      else toast.error(error.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Page
      eyebrow="Administration"
      title="Referral management"
      description="Review referral attribution, track milestone qualification, and manage referrer codes."
      actions={
        <>
          <button
            className="fin-button secondary"
            onClick={refresh}
            disabled={list.loading || leaderboard.loading}
          >
            Refresh
          </button>
          <button
            className="fin-button"
            onClick={exportCsv}
            disabled={exporting}
          >
            {exporting ? "Preparing CSV…" : "Download payout CSV"}
          </button>
        </>
      }
    >
      <Panel title="Referrer leaderboard">
        <LoadState {...leaderboard} />
        {!leaderboard.loading && !leaderboard.error && (
          <DataTable
            rowKey="referrerMerchantId"
            rows={leaderboardData}
            columns={[
              { key: "referrerName", label: "BD / merchant" },
              { key: "merchantCode", label: "Merchant code" },
              { key: "referralCode", label: "Referral code" },
              { key: "totalReferred", label: "Total signed up" },
              { key: "totalOnboarded", label: "KYC approved" },
              { key: "totalTransacting", label: "Active transacting" },
              {
                key: "estimatedPayout",
                label: "Est. total MD payout",
                render: (row) => money(estimatedPayout(row)),
              },
              {
                key: "actions",
                label: "Action",
                render: (row) => (
                  <button
                    className="table-link"
                    onClick={() => {
                      mutation.reset();
                      setSelectedReferrer({
                        referrerMerchantId: row.referrerMerchantId,
                        referrerName: row.referrerName,
                        referralCode: row.referralCode || "",
                      });
                    }}
                  >
                    Assign code
                  </button>
                ),
              },
            ]}
          />
        )}
      </Panel>

      <Panel
        title="Referral attribution"
        subtitle="All referred merchants and their onboarding and transaction milestones."
      >
        <form
          className="toolbar"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(0);
            setReferrerId(referrerIdInput.trim());
          }}
        >
          <Field
            label="Referrer merchant ID"
            name="referrerId"
            value={referrerIdInput}
            onChange={(event) =>
              setReferrerIdInput(event.target.value.replace(/\D/g, ""))
            }
            placeholder="All referrers"
          />
          <Field
            label="Referral status"
            name="status"
            value={status}
            options={REFERRAL_STATUSES}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(0);
            }}
          />
          <button className="fin-button secondary" type="submit">
            Apply filters
          </button>
          <button
            className="fin-button secondary"
            type="button"
            onClick={() => {
              setReferrerIdInput("");
              setReferrerId("");
              setStatus("");
              setPage(0);
            }}
          >
            Clear
          </button>
        </form>

        <LoadState {...list} />
        {!list.loading && !list.error && (
          <>
            <Stats
              items={[
                ["Matching referrals", list.data?.totalElements ?? referrals.length],
                ["Page", `${page + 1}${totalPages ? ` of ${totalPages}` : ""}`],
              ]}
            />
            <DataTable
              rowKey="merchantId"
              rows={referrals}
              columns={[
                {
                  key: "referrerName",
                  label: "Referrer",
                  render: (row) =>
                    row.referrerMerchantName ||
                    `Merchant ${row.referrerMerchantId ?? "—"}`,
                },
                { key: "referralCodeUsed", label: "Code used" },
                { key: "merchantName", label: "Referred merchant" },
                { key: "merchantCode", label: "Merchant code" },
                {
                  key: "signupDate",
                  label: "Signup date",
                  render: (row) => dateTime(row.signupDate || row.referredAt),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (row) => <ReferralStatusBadge value={row.status} />,
                },
                {
                  key: "milestoneOne",
                  label: "Milestone 1",
                  render: () => <Badge value="ELIGIBLE" />,
                },
                {
                  key: "milestoneTwo",
                  label: "Milestone 2",
                  render: (row) => (
                    <Badge
                      value={
                        row.approved && row.live && row.transacting
                          ? "ELIGIBLE"
                          : "PENDING"
                      }
                    />
                  ),
                },
                {
                  key: "actions",
                  label: "Referrer code",
                  render: (row) =>
                    row.referrerMerchantId ? (
                      <button
                        className="table-link"
                        onClick={() => {
                          mutation.reset();
                          setSelectedReferrer({
                            referrerMerchantId: row.referrerMerchantId,
                            referrerName:
                              row.referrerMerchantName ||
                              `Merchant ${row.referrerMerchantId}`,
                            referralCode: referrerCode(
                              leaderboardData,
                              row.referrerMerchantId
                            ),
                          });
                        }}
                      >
                        Assign code
                      </button>
                    ) : (
                      "—"
                    ),
                },
              ]}
            />
            <div className="toolbar">
              <button
                className="fin-button secondary"
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                disabled={page === 0}
              >
                Previous
              </button>
              <span>
                Page {page + 1}
                {totalPages ? ` of ${totalPages}` : ""}
              </span>
              <Field
                label="Rows per page"
                name="size"
                value={size}
                options={["20", "50", "100", "200"]}
                onChange={(event) => {
                  setSize(Number(event.target.value));
                  setPage(0);
                }}
              />
              <button
                className="fin-button secondary"
                onClick={() => setPage((current) => current + 1)}
                disabled={
                  totalPages != null
                    ? page + 1 >= totalPages
                    : referrals.length < size
                }
              >
                Next
              </button>
            </div>
          </>
        )}
      </Panel>
      {selectedReferrer && (
        <ReferralCodeDialog
          referrer={selectedReferrer}
          onClose={() => {
            setSelectedReferrer(null);
            mutation.reset();
          }}
          onSave={assignCode}
          busy={mutation.busy}
          error={mutation.error}
        />
      )}
    </Page>
  );
}
